import { createContext, useContext, useState, useEffect, ReactNode } from "react";

type CalculatorTheme = "dark" | "light";

interface CalculatorThemeContextType {
  theme: CalculatorTheme;
  toggleTheme: () => void;
  isDark: boolean;
}

const CalculatorThemeContext = createContext<CalculatorThemeContextType | undefined>(undefined);

const STORAGE_KEY = "calculator-theme";

export const CalculatorThemeProvider = ({ children }: { children: ReactNode }) => {
  const [theme, setTheme] = useState<CalculatorTheme>(() => {
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(STORAGE_KEY);
      return (stored as CalculatorTheme) || "light";
    }
    return "light";
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "dark" ? "light" : "dark"));
  };

  return (
    <CalculatorThemeContext.Provider value={{ theme, toggleTheme, isDark: theme === "dark" }}>
      {children}
    </CalculatorThemeContext.Provider>
  );
};

export const useCalculatorTheme = () => {
  const context = useContext(CalculatorThemeContext);
  if (!context) {
    throw new Error("useCalculatorTheme must be used within a CalculatorThemeProvider");
  }
  return context;
};
